import type { DocSection } from '../types'

export const startSection: DocSection = {
  id: 'demarrer',
  title: 'Bien démarrer',
  icon: 'home',
  accent: '#7C3AED',
  summary: "Découvrir PexiTalk, l'interface et les rôles. À lire en premier.",
  minRole: 'agent',
  articles: [
    {
      id: 'cest-quoi',
      title: "Qu'est-ce que PexiTalk ?",
      summary: "Le centre d'appels tout-en-un : appels sortants/entrants, fiches, qualité.",
      icon: 'phone',
      blocks: [
        { type: 'p', text: "PexiTalk est un **logiciel de centre d'appels**. Il réunit, au même endroit : votre **softphone** (téléphone logiciel intégré, pas besoin d'appareil physique), la **file des numéros à appeler**, la **fiche du contact**, les **scripts**, la **qualification de l'appel** (disposition), les **rappels et rendez-vous**, l'**historique** avec enregistrements, la **messagerie interne**, et — pour les responsables — la **supervision** et les **statistiques**." },
        { type: 'p', text: "Selon votre **rôle**, vous voyez plus ou moins de fonctions. Cette aide s'adapte automatiquement : vous ne voyez ici que les rubriques qui vous concernent." },
        { type: 'callout', variant: 'tip', title: 'En une phrase', text: "Vous vous connectez, vous passez « Disponible », vous appelez (vous-même ou via une campagne), vous qualifiez chaque appel, et tout est tracé." },
        { type: 'seealso', items: [
          { to: 'demarrer/interface', label: "Comprendre l'interface" },
          { to: 'demarrer/roles', label: 'Les rôles et leurs accès' },
          { to: 'appels', label: 'Passer & recevoir des appels' },
        ] },
      ],
    },
    {
      id: 'interface',
      title: "L'interface en un coup d'œil",
      summary: 'Barre latérale, barre du haut, softphone, espace de travail.',
      icon: 'grid',
      blocks: [
        { type: 'h', text: 'Les 4 zones de l\'écran' },
        { type: 'kv', items: [
          { k: 'Barre latérale (gauche)', v: "Le menu de navigation, organisé en **sections ouvrables/fermables** (cliquez sur le titre d'une section — Outils, Admin… — pour la replier ; votre choix est mémorisé). Les entrées affichées dépendent de votre rôle. Menu entier repliable via la double-flèche en haut. Un badge rouge signale les rappels en retard et les messages non lus." },
          { k: 'Barre du haut', v: "**Recherche globale** (clic ou **⌘K / Ctrl+K**) pour sauter vers n'importe quelle page ou chercher dans l'aide, **sélecteur de statut** (votre disponibilité), bouton **Aide** pour [[communication/entraide|lever la main]], bouton **?** qui ouvre l'aide de la page courante, cloche d'alertes (superviseurs), et votre menu utilisateur (Paramètres, Aide, Déconnexion)." },
          { k: 'Softphone (droite)', v: "Le **panneau téléphone** : clavier pour composer, appel en cours, mise en attente, transfert, mute. En fin d'appel, la **fiche de clôture** (disposition) s'y affiche." },
          { k: "Espace de travail d'appel (centre)", v: "Pendant un appel, la fiche du contact s'agrandit : coordonnées, **script**, champs à remplir, notes, prise de rendez-vous." },
        ] },
        { type: 'callout', variant: 'info', title: 'Mode compact', text: "Si vous rétrécissez la fenêtre sous ~560 px, PexiTalk masque tout sauf le softphone — pratique pour garder le téléphone visible à côté d'un autre logiciel." },
      ],
    },
    {
      id: 'roles',
      title: 'Les rôles et leurs accès',
      summary: 'Agent, Superviseur, Administrateur, Propriétaire — qui peut quoi.',
      icon: 'shield',
      blocks: [
        { type: 'p', text: "PexiTalk a **4 rôles**, du plus restreint au plus large. Chaque rôle hérite des accès du précédent." },
        { type: 'table', headers: ['Rôle', 'Périmètre', 'Exemples d\'accès'], rows: [
          ['**Agent**', 'Son propre travail', "Sa file, ses appels, ses rappels, ses RDV, son historique, ses scripts, la messagerie."],
          ['**Superviseur** (team_admin)', 'Son équipe', "Tout l'agent + campagnes, carnet, formulaires, supervision live, temps agents, statistiques, gestion des utilisateurs de son équipe."],
          ['**Administrateur**', 'Tout le compte', "Tout le superviseur + téléphonie (opérateurs et numéros), liste noire, listes de numéros, distribution d'appels, automatisations, fiches & contacts."],
          ['**Propriétaire** (owner)', 'Le compte entier + sécurité', "Tout l'administrateur + gestion des administrateurs, documentation technique (API)."],
        ] },
        { type: 'callout', variant: 'info', title: 'Bon à savoir', text: "Votre rôle est affiché en bas de la barre latérale (pastille colorée). Si une page vous est refusée, c'est qu'elle dépasse votre rôle — demandez à un responsable." },
      ],
    },
    {
      id: 'connexion',
      title: 'Se connecter et démarrer sa journée',
      summary: 'Login, statut Disponible, téléphone prêt.',
      icon: 'user-check',
      blocks: [
        { type: 'steps', items: [
          "Ouvrez PexiTalk et connectez-vous avec votre **identifiant** et **mot de passe** (fournis par votre responsable).",
          "Vérifiez que le **softphone** est prêt : le panneau de droite doit indiquer que la ligne est connectée. S'il reste « non connecté », voir [[depannage/softphone|Dépannage du softphone]].",
          "Passez votre statut sur **Disponible** (sélecteur en haut) pour pouvoir recevoir/passer des appels.",
          "Ouvrez **Espace d'appel** ou une **campagne** pour commencer à appeler.",
        ] },
        { type: 'callout', variant: 'tip', title: 'Vous restez connecté', text: "La session **ne se déconnecte plus toute seule** : une fois connecté, vous le restez (comme un réseau social), même en fermant et rouvrant l'application. La session ne se termine que si vous cliquez sur **Déconnexion**, ou si vous vous reconnectez sur un autre appareil." },
        { type: 'callout', variant: 'warning', title: 'Mot de passe oublié', text: "Utilisez « Mot de passe oublié » sur l'écran de connexion. Un seul appareil par compte : vous connecter ailleurs **déconnecte** votre session précédente." },
        { type: 'callout', variant: 'warning', title: 'Heures de travail', text: "Votre compte peut être limité à une **plage horaire / jours autorisés**. Hors de cette plage, la connexion est refusée et une session ouverte est fermée (les responsables ne sont pas concernés)." },
      ],
    },
    {
      id: 'parametres',
      title: "La page Paramètres",
      summary: 'Votre compte, vos périphériques audio, la configuration.',
      icon: 'settings',
      blocks: [
        { type: 'p', text: "La page **Paramètres** (barre latérale, ou menu utilisateur) regroupe vos réglages personnels et — selon votre rôle — la configuration de l'application." },
        { type: 'kv', items: [
          { k: 'Périphériques audio', v: "Choisissez votre **micro** et votre **haut-parleur** (casque), avec un bouton de test. Premier réflexe si on ne vous entend pas — voir [[depannage/appels|Dépannage des appels]]." },
          { k: 'Compte', v: "Vos informations (identifiant, nom, email, rôle, extension, caller-ID sortant) et le bouton de **déconnexion**." },
          { k: 'Softphone SIP (admin)', v: "Le serveur téléphonique auquel se connectent tous les agents." },
          { k: 'Fuseau horaire (propriétaire)', v: "Le fuseau du compte, utilisé par les horaires de campagne et les rapports." },
          { k: 'API (admin)', v: "L'adresse du serveur et l'accès à la documentation technique." },
        ] },
      ],
    },
    {
      id: 'glossaire',
      title: 'Glossaire',
      summary: 'Les mots de PexiTalk expliqués simplement.',
      icon: 'book-open',
      blocks: [
        { type: 'p', text: "Quelques termes que vous croiserez dans l'application :" },
        { type: 'kv', items: [
          { k: 'Campagne (fiche)', v: "Un groupe de numéros à appeler qui partagent un même projet et une même configuration. Voir [[campagnes|Campagnes]]." },
          { k: 'File (d\'appels)', v: "Les numéros qui vous sont attribués et qui vous attendent. « Espace d'appel » dans le menu." },
          { k: 'Disposition', v: "Le **résultat** que vous donnez à un appel à la fin (Intéressé, Messagerie, Vente…). Voir [[dispositions|Qualifier un appel]]." },
          { k: 'Rappel', v: "Un numéro à recomposer à une date/heure précise. Voir [[rappels|Rappels & rendez-vous]]." },
          { k: 'Rendez-vous (RDV)', v: "Un engagement obtenu auprès du contact (démonstration, visite…) — plus qu'un simple rappel." },
          { k: 'Statut', v: "Votre disponibilité (Disponible, Pause, Hors ligne…). Voir [[statuts|Statuts & disponibilité]]." },
          { k: 'Softphone', v: "Le téléphone intégré (panneau de droite). Aucun appareil à brancher." },
          { k: 'Numéro présenté', v: "Le numéro qui s'affiche chez la personne que vous appelez." },
          { k: 'Liste noire', v: "Les numéros à ne plus jamais appeler. Voir [[blacklist|Liste noire]]." },
          { k: 'Aperçu / Progressif', v: "Deux façons de composer : vous-même (aperçu) ou le système (progressif). Voir [[dialer|Mode progressif]]." },
        ] },
      ],
    },
  ],
}
